// src/pages/paidProject/Annexure 1/ProjectedProfitability.jsx
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "../../../api";

// ─── Depreciation helper from Depreciation.jsx ─────────────
function calcFullYear(cost, rate, yearIndex) {
  if (yearIndex === 0) return 0;
  let remaining = cost,
    dep = 0;
  for (let i = 1; i <= yearIndex; i++) {
    dep = (remaining * rate) / 100;
    remaining -= dep;
  }
  return dep;
}

// at the top, alongside your other imports
function generateMonthLabels(startStr, length) {
  if (!startStr || !startStr.includes("-")) return [];
  const [startM, startY] = startStr.split("-").map(Number);
  const months = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  let m = startM - 1,
    y = startY;
  return Array.from({ length }, () => {
    const label = `${months[m]}-${y}`;
    m = (m + 1) % 12;
    if (m === 0) y++;
    return label;
  });
}

export default function ProjectedProfitability() {
  const { projectId } = useParams();
  const [data, setData] = useState(null);

  // ─── 2) Fetch API inputs ─────────────────────────────────
  useEffect(() => {
    API.get(`/user/project-inputs/${projectId}`)
      .then(({ data }) => setData(data))
      .catch((err) => console.error("Failed to load project inputs", err));
  }, [projectId]);

  // ─── 3) Destructure API data with defaults ───────────────
  const {
    projectYears = [],
    capacityUtilizationPerYear = [],
    rmConsumptionInputs = [],
    termLoanInput = {},
    projectedProfitabilityInputs = {},
    depreciationData = [],
    workingCapitalInputs = {},
    totalAnnualSales: totalAnnualSalesLS = [],
    grandTotalCostOfProduction: costProdLS = [],
    averageCostOfProdPerMT: avgCostPerMTLS = [],
    workingCapitalBank,
    totalClosingStock: closingPerYearLS = [],
  } = data || {};

  // ─── 4) Depreciation cost lookup (from Depreciation.jsx) ─
  const getCostByParticular = useCallback(
    (p) => {
      if (!data) return 0;
      const {
        costProjectInputs = {},
        plantMachineryData = [],
        plantContingencies,
        plantGstPercent,
        furnitureAndFixtures = [],
        otherFixedAssets = [],
        computers = [],
        preliminaryExpenses = [],
      } = data;
      const keyMap = {
        "Land & Site Development (On Lease)": "landDevelopment",
        "Civil Construction (On Lease)": "civilConstruction",
      };
      if (keyMap[p] && costProjectInputs[keyMap[p]] != null) {
        return parseFloat(costProjectInputs[keyMap[p]]) || 0;
      }
      switch (p) {
        case "Plant & Machinery": {
          const subtotal = plantMachineryData.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
          const cont = (subtotal * (parseFloat(plantContingencies) || 0)) / 100;
          const tax =
            ((subtotal + cont) * (parseFloat(plantGstPercent) || 0)) / 100;
          return subtotal + cont + tax;
        }
        case "Furniture and Fixture":
          return furnitureAndFixtures.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        case "Miscellaneous Fixed Assets":
          return otherFixedAssets.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        case "Computer and Software":
          return computers.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        case "Preliminary Expenses":
          return preliminaryExpenses.reduce(
            (s, r) => s + (parseFloat(r.amount) || 0),
            0
          );
        default:
          return 0;
      }
    },
    [data]
  );

  // ─── 5) Parse mm-yyyy and months active ───────────────────
  const prodStart = useMemo(() => {
    const [m, y] = (termLoanInput.startMonth || "").split("-").map(Number);
    return m && y ? { month: m, year: y } : null;
  }, [termLoanInput.startMonth]);

  const getMonthsActive = useCallback(
    (fyLabel) => {
      if (!prodStart) return 12;
      const fyStart = parseInt(fyLabel, 10);
      const fyEnd = fyStart + 1;
      const raw = (fyEnd - prodStart.year) * 12 + (3 - prodStart.month + 1);
      return Math.max(0, Math.min(raw, 12));
    },
    [prodStart]
  );

  // ─── 6) Installed capacity & production MT ──────────────
  const installedArr = useMemo(
    () =>
      rmConsumptionInputs.map((r) => {
        const d = Number(r.workingDays) || 0;
        const h = Number(r.workingHoursPerDay) || 0;
        const o = Number(r.outputPerHour) || 0;
        return (d * h * o) / 1000;
      }),
    [rmConsumptionInputs]
  );

  const totalInstalled = useMemo(
    () => installedArr.reduce((sum, v) => sum + v, 0),
    [installedArr]
  );

  const productionMT = useMemo(
    () =>
      projectYears.map((fy, yi) => {
        const util = Number(capacityUtilizationPerYear[yi]) || 0;
        const months = getMonthsActive(fy);
        return +((totalInstalled * util * months) / (100 * 12)).toFixed(2);
      }),
    [projectYears, capacityUtilizationPerYear, totalInstalled, getMonthsActive]
  );

  // ─── 7) Input adjustment percentages ─────────────────────
  const leaseRent = +projectedProfitabilityInputs.leaseRent || 0;
  const sellingPct =
    +projectedProfitabilityInputs.sellingAndDistributionExpense || 0;
  const prelimExpensesWrittenOff =
    +projectedProfitabilityInputs.preliminaryExpensesWrittenOff || 0;
  const provisionForTaxPct = +projectedProfitabilityInputs.provisionForTax || 0;
  const ccPct = +workingCapitalInputs.interestOnCC || 0;

  // ─── 8) Depreciation totals per FY ───────────────────────
  const depreciationTotals = useMemo(() => {
    return projectYears.map((fy, yi) => {
      const monthlyFactor = getMonthsActive(fy) / 12;
      return depreciationData.reduce((sum, row) => {
        const cost = getCostByParticular(row.particular);
        const fullDep = calcFullYear(cost, row.taxPercent, yi);
        return sum + fullDep * monthlyFactor;
      }, 0);
    });
  }, [projectYears, depreciationData, getCostByParticular, getMonthsActive]);

  // ─── 9) FG stock calculations ───────────────────────────
  const closingStockFG = useMemo(
    () =>
      projectYears.map((_, i) => {
        const cs = +closingPerYearLS[i] || 0;
        const avgC = +avgCostPerMTLS[i] || 0;
        return +((cs * avgC) / 100000).toFixed(2);
      }),
    [projectYears, closingPerYearLS, avgCostPerMTLS]
  );

  const openingStockFG = useMemo(
    () => closingStockFG.map((_, i) => (i > 0 ? closingStockFG[i - 1] : 0)),
    [closingStockFG]
  );

  // ─── 10) Net COGS & Gross Profit ────────────────────────
  const netCOGS = useMemo(
    () =>
      projectYears.map((_, i) => {
        const cp = +costProdLS[i] || 0;
        return +(cp + openingStockFG[i] - closingStockFG[i]).toFixed(2);
      }),
    [projectYears, costProdLS, openingStockFG, closingStockFG]
  );

  const grossProfit = useMemo(
    () =>
      projectYears.map((_, i) => {
        const sales = +totalAnnualSalesLS[i] || 0;
        return +(sales - netCOGS[i]).toFixed(2);
      }),
    [projectYears, totalAnnualSalesLS, netCOGS]
  );

  // ─── 11) Other adjustments & net PBT/PAT ───────────────
  const sgaExpenses = useMemo(
    () =>
      projectYears.map((_, i) => {
        const sales = +totalAnnualSalesLS[i] || 0;
        return +((sales * sellingPct) / 100).toFixed(2);
      }),
    [projectYears, totalAnnualSalesLS, sellingPct]
  );

  // ─── Recompute Working Capital (Bank) here ───────────────────
  const wcrByYear = useMemo(() => {
    // your WCR series was saved in LS as 'workingCapitalRequirement'
    const wcr = workingCapitalBank || "[]";
    return projectYears.map((_, i) => Number(wcr[i]) || 0);
  }, [projectYears]);

  // ─── Now CC Charges = WorkingCapital(Bank) × interestOnCC% ──
  const ccCharges = useMemo(() => {
    return wcrByYear.map((b) => +((b * ccPct) / 100).toFixed(2));
  }, [wcrByYear, ccPct]);

  // Pull emiSchedule & start-month from your API data
  const term = data?.termLoanInput || {};
  const rawStart =
    term.pStartMonth && term.pStartMonth.includes("-")
      ? term.pStartMonth
      : term.startMonth || "";

  const financeSummary = useMemo(() => {
    let emi = Array.isArray(term.emiSchedule) ? term.emiSchedule : [];

    /* // fallback to localStorage
    if (!emi.length) {
      const local = JSON.parse(localStorage.getItem('emiSchedule') || '[]');
      if (Array.isArray(local) && local.length) emi = local;
    } */
    if (!emi.length || !rawStart) return [];

    const monthLabels = generateMonthLabels(rawStart, emi.length);
    const monthsMap = {
      Jan: 1,
      Feb: 2,
      Mar: 3,
      Apr: 4,
      May: 5,
      Jun: 6,
      Jul: 7,
      Aug: 8,
      Sep: 9,
      Oct: 10,
      Nov: 11,
      Dec: 12,
    };
    const bucket = {};
    emi.forEach((row, idx) => {
      const lbl = monthLabels[idx] || "";
      const [monStr, yrStr] = lbl.split("-");
      const mon = monthsMap[monStr] || 0;
      const yr = parseInt(yrStr, 10) || 0;
      const fyStart = mon >= 4 ? yr : yr - 1;
      const fyLabel = `${fyStart}-${String(fyStart + 1).slice(-2)}`;

      if (!bucket[fyLabel]) {
        bucket[fyLabel] = {
          year: fyLabel,
          totalPrincipal: 0,
          totalInterest: 0,
          closingBalance: 0,
        };
      }
      bucket[fyLabel].totalPrincipal += row.principal || 0;
      bucket[fyLabel].totalInterest += row.interest || 0;
      bucket[fyLabel].closingBalance = row.closingBalance || 0;
    });
    return Object.values(bucket);
  }, [term.emiSchedule, term.startMonth, term.pStartMonth]);

  const termLoanCharges = useMemo(
    () => financeSummary.map((fy) => +(fy.totalInterest || 0)),
    [financeSummary]
  );

  const totalAdjustments = useMemo(
    () =>
      projectYears.map((_, i) => {
        const lease = i > 0 ? leaseRent : 0;
        const dep = +depreciationTotals[i] || 0;
        return +(
          lease +
          sgaExpenses[i] +
          dep +
          (i > 0 ? prelimExpensesWrittenOff : 0) +
          ccCharges[i] +
          termLoanCharges[i]
        ).toFixed(2);
      }),
    [
      projectYears,
      leaseRent,
      sgaExpenses,
      depreciationTotals,
      prelimExpensesWrittenOff,
      ccCharges,
      termLoanCharges,
    ]
  );

  const netProfitBeforeTax = useMemo(
    () =>
      projectYears.map(
        (_, i) => +(grossProfit[i] - totalAdjustments[i]).toFixed(2)
      ),
    [projectYears, grossProfit, totalAdjustments]
  );

  const provisionForTax = useMemo(
    () =>
      netProfitBeforeTax.map(
        (npt) => +((npt * provisionForTaxPct) / 100).toFixed(2)
      ),
    [netProfitBeforeTax, provisionForTaxPct]
  );

  const netProfitAfterTax = useMemo(
    () =>
      netProfitBeforeTax.map(
        (npt, i) => +(npt - provisionForTax[i]).toFixed(2)
      ),
    [netProfitBeforeTax, provisionForTax]
  );

  // ─── 13) Persist PBT / Prov / PAT ─────────────────────────
  useEffect(() => {
    // only once we have data & our three arrays computed
    if (!data) return;

    API.put(`/user/project-inputs/${projectId}`, {
      netProfitBeforeTax,
      provisionForTax,
      netProfitAfterTax,
    })
      .then(() => console.log("✅ Profitability series saved"))
      .catch((err) =>
        console.error("❌ Failed to save profitability series", err)
      );
  }, [data, netProfitBeforeTax, provisionForTax, netProfitAfterTax, projectId]);

  // ─── 12) Loading guard ───────────────────────────────────
  if (!data) {
    return <p className="p-6 text-center">Loading...</p>;
  }

  // ─── 13) Formatter ───────────────────────────────────────
  const fmt = (x) =>
    isNaN(x) ? "-" : x.toLocaleString("en-IN", { maximumFractionDigits: 2 });

  // ─── 14) Render ──────────────────────────────────────────
  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Projected Profitability</h1>

      {/* ─── Input Adjustments ─────────────────────────────── */}
      <h2 className="text-xl font-semibold mb-2">Input Adjustments</h2>
      <div className="overflow-x-auto mb-8">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">Detail</th>
              {projectYears.map((yr, i) => (
                <th key={i} className="px-4 py-2 text-center">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr className="bg-white">
              <td className="px-4 py-2">Lease Rent</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(i > 0 ? leaseRent : 0)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">SG&A Expenses</td>
              {sgaExpenses.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-white">
              <td className="px-4 py-2">Depreciation</td>
              {depreciationTotals.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Prelim. Expenses Written Off</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(i > 0 ? prelimExpensesWrittenOff : 0)}
                </td>
              ))}
            </tr>
            <tr className="bg-white">
              <td className="px-4 py-2">Financial Charges (CC)</td>
              {ccCharges.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Financial Charges (Term Loan)</td>
              {termLoanCharges.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-100 font-semibold">
              <td className="px-4 py-2">Total</td>
              {totalAdjustments.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* ─── Main Profitability ─────────────────────────────── */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left">Details</th>
              {projectYears.map((yr, i) => (
                <th key={i} className="px-4 py-2 text-center">
                  {yr}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            <tr className="bg-white">
              <td className="px-4 py-2">Capacity Util (%)</td>
              {capacityUtilizationPerYear.map((u, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(u.value ?? u)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Output Per Annum (MT)</td>
              {productionMT.map((mt, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(mt)}
                </td>
              ))}
            </tr>
            <tr className="bg-white">
              <td className="px-4 py-2">Annual Sales (lacs)</td>
              {totalAnnualSalesLS.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-white italic">
              <td className="px-4 py-2">COST OF GOODS SOLD</td>
              {projectYears.map((_, i) => (
                <td key={i} className="px-4 py-2"></td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Cost of Prod (A+B)</td>
              {costProdLS.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-white">
              <td className="px-4 py-2">Opening Stock FG &amp; WIP</td>
              {openingStockFG.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-gray-50">
              <td className="px-4 py-2">Closing Stock FG &amp; WIP</td>
              {closingStockFG.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-blue-50 font-semibold">
              <td className="px-4 py-2">Net COGS</td>
              {netCOGS.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-green-50 font-semibold">
              <td className="px-4 py-2">Gross Profit</td>
              {grossProfit.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-yellow-50 font-semibold">
              <td className="px-4 py-2">Net Profit Before Tax</td>
              {netProfitBeforeTax.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-red-50 font-semibold">
              <td className="px-4 py-2">Provision for Tax</td>
              {provisionForTax.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
            <tr className="bg-purple-50 font-semibold">
              <td className="px-4 py-2">Net Profit After Tax</td>
              {netProfitAfterTax.map((v, i) => (
                <td key={i} className="px-4 py-2 text-center">
                  {fmt(v)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}
